import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import AdminLayout from '@/components/admin/AdminLayout';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import {
  ArrowLeft,
  Bot,
  Settings,
  MessageSquare,
  Shield,
  FileText,
  Send,
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle,
  Eye,
  Lock,
  Rocket,
  Code,
   ExternalLink,
   Copy,
   Check,
} from 'lucide-react';
import BotQARunner from '@/components/admin/bot-detail/BotQARunner';
import BotPublishing from '@/components/admin/bot-detail/BotPublishing';
import BotDeploy from '@/components/admin/bot-detail/BotDeploy';

interface BotDetail {
  id: string;
  tenant_id: string;
  template_id: string | null;
  governance_pack_id: string | null;
  source_bundle_id: string | null;
  environment: string | null;
  mode: string | null;
  status: string | null;
  widget_settings: any;
  nuance_pack: any;
  email_summary_enabled: boolean | null;
  newsletter_optin_enabled: boolean | null;
  summary_forward_email: string | null;
  webhook_url: string | null;
  created_at: string | null;
}

interface TemplateInfo {
  id: string;
  purpose: string;
  industry: string;
}

interface GovernancePackInfo {
  id: string;
  industry: string;
  version: number | null;
  public_trust_charter: string | null;
  internal_rules: any;
}

interface SourceBundleInfo {
  id: string;
  name: string;
  status: string | null;
}

interface TenantInfo {
  id: string;
  name: string;
  industry: string | null;
}

// Status configuration with icons
const statusConfig: Record<string, { color: string; icon: React.ReactNode; description: string }> = {
  draft: { 
    color: 'bg-muted text-muted-foreground', 
    icon: <Clock className="w-4 h-4" />,
    description: 'Bot is being configured. Not yet testable.'
  },
  qa_failed: { 
    color: 'bg-destructive/10 text-destructive', 
    icon: <XCircle className="w-4 h-4" />,
    description: 'QA tests failed. Review and fix issues before retesting.'
  },
  ready_for_review: { 
    color: 'bg-amber-500/10 text-amber-600', 
    icon: <Eye className="w-4 h-4" />,
    description: 'Ready for client review and approval.'
  },
  changes_requested: { 
    color: 'bg-orange-500/10 text-orange-600', 
    icon: <AlertCircle className="w-4 h-4" />,
    description: 'Client requested changes. Return to draft to edit.'
  },
  approved: { 
    color: 'bg-blue-500/10 text-blue-600', 
    icon: <CheckCircle className="w-4 h-4" />,
    description: 'Approved and ready for publishing.'
  },
  live: { 
    color: 'bg-green-500/10 text-green-600', 
    icon: <CheckCircle className="w-4 h-4" />,
    description: 'Bot is live and serving visitors.'
  },
};

const environmentColors: Record<string, string> = {
  preview: 'bg-purple-500/10 text-purple-600',
  production: 'bg-green-500/10 text-green-600',
};

// Lifecycle transitions
const lifecycleTransitions: Record<string, string[]> = {
  draft: ['qa_failed', 'ready_for_review'],
  qa_failed: ['draft'],
  ready_for_review: ['changes_requested', 'approved'],
  changes_requested: ['draft'],
  approved: ['live'],
  live: [],
};

const AdminBotDetail: React.FC = () => {
  const { botId } = useParams<{ botId: string }>();
  const [bot, setBot] = useState<BotDetail | null>(null);
  const [template, setTemplate] = useState<TemplateInfo | null>(null);
  const [governancePack, setGovernancePack] = useState<GovernancePackInfo | null>(null);
  const [sourceBundle, setSourceBundle] = useState<SourceBundleInfo | null>(null);
  const [tenant, setTenant] = useState<TenantInfo | null>(null);
  const [approvedAssetsCount, setApprovedAssetsCount] = useState(0);
  const [loading, setLoading] = useState(true);
   const [linkCopied, setLinkCopied] = useState(false);
 
   const previewUrl = `${window.location.origin}/preview/${botId}`;
 
   const handleCopyLink = async () => {
     try {
       await navigator.clipboard.writeText(previewUrl);
       setLinkCopied(true);
       setTimeout(() => setLinkCopied(false), 2000);
     } catch (error) {
       console.error('Failed to copy:', error);
     }
   };

  useEffect(() => {
    const fetchBot = async () => {
      if (!botId) return;

      try {
        // Fetch bot details
        const { data: botData, error: botError } = await supabase
          .from('bots')
          .select('*')
          .eq('id', botId)
          .single();

        if (botError) throw botError;
        setBot(botData);

        // Fetch related data in parallel - use individual queries then await
        const tenantPromise = supabase.from('advisory_tenants').select('id, name, industry').eq('id', botData.tenant_id).single();
        
        const templatePromise = botData.template_id 
          ? supabase.from('templates').select('id, purpose, industry').eq('id', botData.template_id).single()
          : Promise.resolve({ data: null });

        const governancePromise = botData.governance_pack_id
          ? supabase.from('governance_packs').select('*').eq('id', botData.governance_pack_id).single()
          : Promise.resolve({ data: null });

        const bundlePromise = botData.source_bundle_id
          ? supabase.from('source_bundles').select('id, name, status').eq('id', botData.source_bundle_id).single()
          : Promise.resolve({ data: null });

        const assetsPromise = supabase.from('tenant_assets')
          .select('id')
          .eq('tenant_id', botData.tenant_id)
          .eq('ingestion_status', 'approved');

        const [tenantRes, templateRes, governanceRes, bundleRes, assetsRes] = await Promise.all([
          tenantPromise,
          templatePromise,
          governancePromise,
          bundlePromise,
          assetsPromise,
        ]);
        
        // Process results
        if (tenantRes.data) setTenant(tenantRes.data);
        if (templateRes.data) setTemplate(templateRes.data);
        if (governanceRes.data) setGovernancePack(governanceRes.data);
        if (bundleRes.data) setSourceBundle(bundleRes.data);
        if (assetsRes.data) setApprovedAssetsCount(assetsRes.data.length);

      } catch (error) {
        console.error('Error fetching bot:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBot();
  }, [botId]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-64 w-full" />
        </div>
      </AdminLayout>
    );
  }

  if (!bot) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <Bot className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium">Bot not found</h3>
          <Link to="/admin/tenants" className="mt-4 inline-flex items-center text-primary hover:underline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to tenants
          </Link>
        </div>
      </AdminLayout>
    );
  }

  const currentStatus = statusConfig[bot.status || 'draft'] || statusConfig.draft;
  const possibleTransitions = lifecycleTransitions[bot.status || 'draft'] || [];
  const isDraft = bot.status === 'draft';
  const isLive = bot.status === 'live';

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link to={`/admin/tenants/${bot.tenant_id}?tab=bots`}>
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          </Link>
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold text-foreground">
                {tenant?.name || 'Unknown'} Bot
              </h1>
              <Badge className={currentStatus.color}>
                {currentStatus.icon}
                <span className="ml-1 capitalize">{(bot.status || 'draft').replace(/_/g, ' ')}</span>
              </Badge>
              <Badge variant="outline" className={environmentColors[bot.environment || 'preview']}>
                {bot.environment || 'preview'}
              </Badge>
               <div className="flex items-center gap-2 ml-auto">
                 <Button 
                   variant="outline" 
                   size="sm"
                   onClick={() => window.open(`/preview/${bot.id}`, '_blank')}
                 >
                   <Eye className="w-4 h-4 mr-2" />
                   Preview Assistant
                   <ExternalLink className="w-3 h-3 ml-1" />
                 </Button>
                 <Button
                   variant="ghost"
                   size="sm"
                   onClick={handleCopyLink}
                   title="Copy preview link"
                 >
                   {linkCopied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                 </Button>
               </div>
            </div>
            <p className="text-muted-foreground mt-1 font-mono text-sm">
              ID: {bot.id}
            </p>
          </div>
        </div>

        {/* Status Description */}
        <Card className="border-l-4 border-l-primary">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <div className="mt-0.5">{currentStatus.icon}</div>
              <div className="flex-1">
                <p className="font-medium capitalize">{(bot.status || 'draft').replace(/_/g, ' ')}</p>
                <p className="text-sm text-muted-foreground">{currentStatus.description}</p>
              </div>
              {possibleTransitions.length > 0 && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>Next:</span>
                  {possibleTransitions.map(t => (
                    <Badge key={t} variant="outline" className="capitalize">
                      {t.replace(/_/g, ' ')}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="composition" className="space-y-4">
          <TabsList>
            <TabsTrigger value="composition">
              <Settings className="w-4 h-4 mr-2" />
              Composition
            </TabsTrigger>
            <TabsTrigger value="qa">
              <CheckCircle className="w-4 h-4 mr-2" />
              QA
            </TabsTrigger>
            <TabsTrigger value="publishing">
              <Rocket className="w-4 h-4 mr-2" />
              Publishing
            </TabsTrigger>
            <TabsTrigger value="deploy" disabled={!isLive}>
              <Code className="w-4 h-4 mr-2" />
              Deploy
            </TabsTrigger>
            <TabsTrigger value="conversations" disabled={isDraft}>
              <MessageSquare className="w-4 h-4 mr-2" />
              Conversations
            </TabsTrigger>
          </TabsList>

          {/* Composition Tab */}
          <TabsContent value="composition" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Template & Governance */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bot className="w-5 h-5" />
                    Purpose Template
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {template ? (
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Purpose</span>
                        <span className="font-medium capitalize">{template.purpose.replace(/_/g, ' ')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Industry</span>
                        <span className="font-medium capitalize">{template.industry}</span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-muted-foreground italic">No template attached</p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    Governance Pack
                    <Lock className="w-4 h-4 text-muted-foreground ml-auto" />
                  </CardTitle>
                  <CardDescription>Read-only. Managed by platform admins.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {governancePack ? (
                    <>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Version</span>
                        <Badge variant="secondary">v{governancePack.version || 1}</Badge>
                      </div>
                      {governancePack.public_trust_charter && (
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">Public Trust Charter</p>
                          <p className="text-sm bg-muted p-2 rounded">{governancePack.public_trust_charter}</p>
                        </div>
                      )}
                      {governancePack.internal_rules?.forbidden_topics && (
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">Forbidden Topics</p>
                          <div className="flex flex-wrap gap-1">
                            {governancePack.internal_rules.forbidden_topics.slice(0, 6).map((topic: string, i: number) => (
                              <Badge key={i} variant="destructive" className="text-xs">{topic}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-muted-foreground italic">No governance pack attached</p>
                  )}
                </CardContent>
              </Card>

              {/* Source Bundle & Knowledge */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Knowledge Sources
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Source Bundle</span>
                    {sourceBundle ? (
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{sourceBundle.name}</span>
                        <Badge variant="outline" className="text-xs">{sourceBundle.status}</Badge>
                      </div>
                    ) : (
                      <span className="text-muted-foreground italic">None</span>
                    )}
                  </div>
                  <Separator />
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Approved Tenant Assets</span>
                    <Badge variant={approvedAssetsCount > 0 ? 'default' : 'secondary'}>
                      {approvedAssetsCount} asset{approvedAssetsCount !== 1 ? 's' : ''}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Only approved FAQs and documents are included in the bot's knowledge pack.
                    Brand kit assets affect styling only.
                  </p>
                </CardContent>
              </Card>

              {/* Destinations */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Send className="w-5 h-5" />
                    Destinations
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Summary Email</span>
                    <span className="font-medium">{bot.summary_forward_email || '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Webhook URL</span>
                    <span className="font-medium font-mono text-xs">
                      {bot.webhook_url ? `${bot.webhook_url.slice(0, 30)}...` : '—'}
                    </span>
                  </div>
                  <Separator />
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Email Summaries</span>
                    <Badge variant={bot.email_summary_enabled ? 'default' : 'secondary'}>
                      {bot.email_summary_enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Newsletter Opt-in</span>
                    <Badge variant={bot.newsletter_optin_enabled ? 'default' : 'secondary'}>
                      {bot.newsletter_optin_enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Nuance Pack */}
            <Card>
              <CardHeader>
                <CardTitle>Nuance Pack</CardTitle>
                <CardDescription>Personality and communication customizations</CardDescription>
              </CardHeader>
              <CardContent>
                {bot.nuance_pack ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <p className="text-sm text-muted-foreground mb-2">Specialisations</p>
                      <div className="flex flex-wrap gap-1">
                        {bot.nuance_pack.specialisations?.map((spec: string, i: number) => (
                          <Badge key={i} variant="secondary">{spec}</Badge>
                        )) || <span className="text-muted-foreground italic">None</span>}
                      </div>
                      {bot.nuance_pack.specialisation_notes && (
                        <p className="text-sm mt-2">{bot.nuance_pack.specialisation_notes}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-2">Tone Modifier</p>
                      <Badge variant="outline" className="capitalize">
                        {bot.nuance_pack.tone_modifier || 'neutral'}
                      </Badge>
                    </div>
                    {bot.nuance_pack.positioning_line && (
                      <div className="md:col-span-2">
                        <p className="text-sm text-muted-foreground mb-1">Positioning Line</p>
                        <p className="text-sm bg-muted p-2 rounded">{bot.nuance_pack.positioning_line}</p>
                      </div>
                    )}
                    {bot.nuance_pack.do_not_mention && (
                      <div className="md:col-span-2">
                        <p className="text-sm text-muted-foreground mb-1">Do Not Mention</p>
                        <p className="text-sm bg-destructive/10 text-destructive p-2 rounded">
                          {bot.nuance_pack.do_not_mention}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-muted-foreground italic">No nuance pack configured</p>
                )}
              </CardContent>
            </Card>

            {/* Actions Card */}
            <Card className="border-dashed">
              <CardContent className="py-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium mb-1">Next Steps</h3>
                    <p className="text-sm text-muted-foreground">
                      {isDraft 
                        ? 'Run QA tests to validate bot behavior before review.'
                        : isLive 
                          ? 'Bot is live and serving visitors.'
                          : 'Complete QA and get client approval to publish.'}
                    </p>
                  </div>
                  {isDraft && (
                    <Button variant="outline" onClick={() => {
                      const qaTab = document.querySelector('[data-state="inactive"][value="qa"]') as HTMLElement;
                      if (qaTab) qaTab.click();
                    }}>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Go to QA
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* QA Tab */}
          <TabsContent value="qa">
            <BotQARunner 
              botId={bot.id}
              tenantId={bot.tenant_id}
              botStatus={bot.status}
              onStatusChange={(newStatus) => setBot({ ...bot, status: newStatus })}
            />
          </TabsContent>

          {/* Publishing Tab */}
          <TabsContent value="publishing">
            <BotPublishing
              botId={bot.id}
              tenantId={bot.tenant_id}
              botStatus={bot.status}
              botEnvironment={bot.environment}
              governancePackId={bot.governance_pack_id}
              onStatusChange={(newStatus) => setBot({ ...bot, status: newStatus, environment: newStatus === 'live' ? 'production' : bot.environment })}
            />
          </TabsContent>

          {/* Deploy Tab */}
          <TabsContent value="deploy">
            <BotDeploy
              botId={bot.id}
              tenantId={bot.tenant_id}
              botStatus={bot.status}
              widgetSettings={bot.widget_settings}
            />
          </TabsContent>

          {/* Conversations Tab */}
          <TabsContent value="conversations">
            <Card>
              <CardContent className="py-12 text-center">
                <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium">Conversation History</h3>
                <p className="text-muted-foreground mt-1">
                  {isDraft 
                    ? 'Bot must pass QA before conversations can be tracked.'
                    : 'Conversation logs will appear here once the bot is live.'}
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminBotDetail;
